<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useUi } from '@/composition/use-ui';
  import { useModal } from '@/composition/use-modal';
  import PrintStationsService from '@/api/services/print-stations.service';
  import { IPrintStation } from '@/interfaces/IPrintStation';
  import { AlertType } from '@/interfaces/IAlert';

  const MAX_STATIONS = 12;
  const DEFAULT_STATION_IDS = [1, 2];

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();

  const stations = computed(() => restaurantStore.printStations);
  const canAdd = computed(() => stations.value.length < MAX_STATIONS);

  const editingStationId = ref<number | null>(null);
  const editName = ref('');
  const adding = ref(false);
  const newName = ref('');
  const saving = ref(false);

  function isDefaultStation(station: IPrintStation) {
    return DEFAULT_STATION_IDS.includes(station.stationId);
  }

  function startRename(station: IPrintStation) {
    editingStationId.value = station.stationId;
    editName.value = station.name;
  }

  function cancelRename() {
    editingStationId.value = null;
    editName.value = '';
  }

  async function confirmRename(station: IPrintStation) {
    if (saving.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    const name = editName.value.trim();
    if (!restaurantId || !name || name === station.name) {
      cancelRename();
      return;
    }

    saving.value = true;
    try {
      await PrintStationsService.rename(restaurantId, station.stationId, name);
      await restaurantStore.loadPrintStations();
      ui.showToast(AlertType.Success, 'Estación guardada');
      cancelRename();
    } catch (e) {
      ui.serverError(e, 'Error al guardar la estación');
    } finally {
      saving.value = false;
    }
  }

  function startAdd() {
    adding.value = true;
    newName.value = '';
  }

  function cancelAdd() {
    adding.value = false;
    newName.value = '';
  }

  async function confirmAdd() {
    if (saving.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    const name = newName.value.trim();
    if (!restaurantId || !name) return;

    saving.value = true;
    try {
      await PrintStationsService.create(restaurantId, name);
      await restaurantStore.loadPrintStations();
      ui.showToast(AlertType.Success, 'Estación creada');
      cancelAdd();
    } catch (e) {
      ui.serverError(e, 'Error al crear la estación');
    } finally {
      saving.value = false;
    }
  }

  async function removeStation(station: IPrintStation) {
    if (saving.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    const fallbackName = restaurantStore.stationName(1);

    const confirmed = await modals.confirm({
      title: `¿Eliminar la estación "${station.name}"?`,
      message:
        `Las categorías y productos que imprimen en "${station.name}" dejarán de hacerlo. ` +
        `Si una categoría imprimía únicamente en esta estación, pasará a imprimir en "${fallbackName}".`,
      acceptText: 'Eliminar',
      acceptColor: 'red',
    });

    if (!confirmed) return;

    saving.value = true;
    try {
      await PrintStationsService.delete(restaurantId, station.stationId);
      await restaurantStore.loadPrintStations();
      ui.showToast(AlertType.Success, 'Estación eliminada');
    } catch (e) {
      ui.serverError(e, 'Error al eliminar la estación');
    } finally {
      saving.value = false;
    }
  }
</script>

<template>
  <v-card elevation="0" class="print-stations-card">
    <v-card-title>Estaciones de impresión</v-card-title>
    <v-card-text>
      <p class="hint">
        Las estaciones son los destinos donde se imprimen las comandas (por
        ejemplo: cocina, barra, parrilla). Puede elegir en qué estaciones se
        imprime cada categoría o producto desde el menú.
      </p>

      <v-list class="stations-list" dense>
        <v-list-item
          v-for="station of stations"
          :key="station.stationId"
          class="station-item"
        >
          <template v-if="editingStationId === station.stationId">
            <v-list-item-content>
              <v-text-field
                v-model="editName"
                label="Nombre de la estación"
                maxlength="64"
                hide-details
                outlined
                dense
                autofocus
                @keydown.enter="confirmRename(station)"
                @keydown.esc="cancelRename"
              />
            </v-list-item-content>
            <v-list-item-action class="station-actions">
              <v-btn
                icon
                color="primary"
                :loading="saving"
                :disabled="!editName.trim()"
                @click="confirmRename(station)"
              >
                <v-icon>mdi-check</v-icon>
              </v-btn>
              <v-btn icon @click="cancelRename">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>
          <template v-else>
            <v-list-item-content>
              <v-list-item-title>
                <v-icon small class="mr-1">mdi-printer</v-icon>
                {{ station.name }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="station-actions">
              <v-btn icon :disabled="saving" @click="startRename(station)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="!isDefaultStation(station)"
                icon
                color="red"
                :disabled="saving"
                @click="removeStation(station)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>
        </v-list-item>
      </v-list>

      <div v-if="adding" class="add-form">
        <v-text-field
          v-model="newName"
          label="Nombre de la nueva estación"
          maxlength="64"
          hide-details
          outlined
          dense
          autofocus
          @keydown.enter="confirmAdd"
          @keydown.esc="cancelAdd"
        />
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!newName.trim()"
          elevation="0"
          @click="confirmAdd"
        >
          Crear
        </v-btn>
        <v-btn text @click="cancelAdd">Cancelar</v-btn>
      </div>
      <v-btn
        v-else
        color="primary"
        rounded
        small
        elevation="0"
        :disabled="!canAdd || saving"
        @click="startAdd"
      >
        <v-icon small class="mr-1">mdi-plus</v-icon>
        Agregar estación
      </v-btn>
      <p v-if="!canAdd" class="hint mt-2">
        Alcanzó el máximo de {{ MAX_STATIONS }} estaciones.
      </p>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
  .print-stations-card {
    .hint {
      @apply tw-text-sm tw-text-neutral-500 tw-mb-2;
    }

    .stations-list {
      @apply tw-mb-2;

      .station-item {
        @apply tw-px-0;

        .station-actions {
          @apply tw-flex tw-flex-row tw-items-center tw-my-0;
        }
      }
    }

    .add-form {
      @apply tw-flex tw-items-center tw-space-x-2;
    }
  }
</style>
